/**
 * Security helper functions for sanitizing user input and preventing common vulnerabilities
 */

/**
 * Sanitizes a string for safe logging by removing or escaping potentially dangerous characters
 * This helps prevent CWE-117: Improper Output Neutralization for Logs
 * Note: This function is kept for potential future use but the main CWE-117 fix
 * was achieved by not passing error objects to console.log at all.
 * @param input - The string to sanitize
 * @returns Sanitized string safe for logging
 */
export const sanitizeForLogging = (input: unknown): string => {
	if (input === null || input === undefined) {
		return '';
	}

	const stringValue = typeof input === 'string' ? input : String(input);

	// Remove carriage returns, newlines, and other control characters
	// that could be used for log injection attacks
	return stringValue
		.replace(/[\r\n]/g, ' ')
		.replace(/[\x00-\x1F\x7F]/g, '')
		.substring(0, 1000); // Limit length to prevent log flooding
};

/**
 * List of allowed URL patterns for redirects
 * This helps prevent CWE-601: URL Redirection to Untrusted Site
 */
const ALLOWED_REDIRECT_PATTERNS = [
	/^https:\/\/[a-z0-9-]*s1\.sovos\.(com|org)(\/.*)?$/i,
	/^https:\/\/[a-z0-9-]*\.sovos\.(com|org)(\/.*)?$/i,
	/^https:\/\/[a-z0-9-]*\.dev\.sovos\.org(\/.*)?$/i,
	/^http:\/\/s1srv01\.dev\.sovos\.org(:\d+)?(\/.*)?$/i,
	/^http:\/\/localhost(:\d+)?(\/.*)?$/i,
];

/**
 * Default fallback URLs for different environments
 */
const getDefaultRedirectUrl = (): string => {
	const hostname = window.location.hostname;
	if (hostname.startsWith('integration') || hostname.includes('int')) {
		return 'https://int-s1.sovos.org';
	}
	if (hostname.includes('qa')) {
		return 'https://qa-s1.dev.sovos.org';
	}
	if (hostname.includes('stg')) {
		return 'https://stg-s1.sovos.com';
	}
	if (hostname.includes('uat')) {
		return 'https://uat-s1.sovos.com';
	}
	if (hostname.includes('localhost') || hostname.includes('dev')) {
		return 'https://dev-s1.sovos.org:8080';
	}
	return 'https://s1.sovos.com';
};

/**
 * Validates and sanitizes a URL for safe redirection
 * This helps prevent CWE-601: URL Redirection to Untrusted Site
 * @param url - The URL to validate
 * @returns The validated URL or a safe default
 */
export const validateRedirectUrl = (url: string | null | undefined): string => {
	if (!url) {
		return getDefaultRedirectUrl();
	}

	// Check if URL matches any allowed pattern
	const isAllowed = ALLOWED_REDIRECT_PATTERNS.some(pattern => pattern.test(url));

	if (isAllowed) {
		return url;
	}

	// Return safe default if URL is not in allowlist
	return getDefaultRedirectUrl();
};

/**
 * Validates a product URL for safe redirection within the Sovos ecosystem
 * @param baseUrl - The base URL of the product
 * @param otp - The OTP token
 * @param org - The organization identifier
 * @returns The validated full URL or null if invalid
 */
export const buildSafeProductUrl = (
	baseUrl: string,
	otp: string,
	org: string | null
): string | null => {
	// Validate the base URL is in our allowlist
	const isAllowed = ALLOWED_REDIRECT_PATTERNS.some(pattern => pattern.test(baseUrl));

	if (!isAllowed) {
		console.warn('Attempted redirect to non-allowed URL blocked');
		return null;
	}

	// Sanitize OTP and org parameters
	const sanitizedOtp = encodeURIComponent(otp);
	const sanitizedOrg = org ? encodeURIComponent(org) : '';

	return `${baseUrl}?otp=${sanitizedOtp}${sanitizedOrg ? `&org=${sanitizedOrg}` : ''}`;
};

/**
 * Sanitizes a filename to prevent path traversal attacks
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
export const sanitizeFilename = (filename: string): string => {
	if (!filename) {
		return 'download';
	}

	// Remove path traversal characters, special characters, and control characters
	return filename
		.replace(/[/\\:*?"<>|]/g, '_')
		.replace(/\.\./g, '_')
		.replace(/[\x00-\x1F\x7F]/g, '')
		.substring(0, 255);
};
